import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from './LanguageSwitcher';
import { BrandMark } from './BrandMark';

/**
 * Editorial header. Looks like a masthead more than a SaaS nav:
 *   - Slate monogram "P" (no gradient, no glow)
 *   - Hairline-only border that thickens on scroll
 *   - Solid slate CTA (no gradient-primary)
 *   - Underline-emerald active marker beneath the focused nav item
 */
export function SiteHeader() {
  const { t } = useTranslation();
  const loc = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItem = (to: string, label: string) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        className={cn(
          'relative text-[13.5px] font-medium tracking-tight transition-colors',
          active ? 'text-foreground' : 'text-foreground/55 hover:text-foreground',
        )}
      >
        {label}
        {active && (
          <motion.span
            layoutId="nav-active"
            className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full bg-emerald-600"
          />
        )}
      </Link>
    );
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-200',
        scrolled
          ? 'bg-background/95 backdrop-blur border-b border-border'
          : 'bg-background border-b border-transparent',
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <BrandMark />

        <nav className="hidden md:flex items-center gap-9">
          {navItem('/pricing', t('nav.pricing'))}
          {navItem('/compare', t('nav.compare'))}
          {navItem('/demo', t('nav.demo'))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center px-3 py-2 text-[13.5px] font-semibold text-foreground/65 hover:text-foreground transition-colors"
          >
            {t('nav.login')}
          </Link>
          <Link
            to="/signup"
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-md bg-foreground text-background text-[13px] font-semibold hover:bg-foreground/90 transition-colors"
          >
            {t('nav.signup')}
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-ink-100 transition"
            aria-label={t('common.menu', { defaultValue: 'Menu' })}
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-border bg-background"
        >
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-3">
            <Link to="/pricing" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.pricing')}</Link>
            <Link to="/compare" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.compare')}</Link>
            <Link to="/demo" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.demo')}</Link>
            <Link to="/login" className="text-sm font-medium" onClick={() => setOpen(false)}>{t('nav.login')}</Link>
            <Link
              to="/signup"
              className="mt-2 inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-semibold bg-foreground text-background"
              onClick={() => setOpen(false)}
            >
              {t('nav.signup')}
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}
